import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Input, showToast } from '@attentive-platform/stem-ui';

import AccountModal from './AccountModal';
import EstimatorsTable from './EstimatorsTable';
import { FormContainer, WideCard } from '../../styled';
import { apiErrorMessage } from '../../Utils/apiErrors';
import {
  companiesFromUpdate,
  estimatorsCount,
  ESTIMATORS_PER_PAGE,
  listSniperEstimators,
  parseEstimators,
  updateSniperEstimator
} from './api';

const InternalTeamAccess = () => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [estimators, setEstimators] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [companiesFor, setCompaniesFor] = useState(null);

  const pageCount = Math.max(1, Math.ceil(totalCount / ESTIMATORS_PER_PAGE));
  const queryRef = useRef(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = search.trim();
      if (queryRef.current === next) return;
      queryRef.current = next;
      setQuery(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadEstimators = useCallback(async ({ q, page: nextPage, signal }) => {
    const { data, error } = await listSniperEstimators({ q, page: nextPage, signal });
    if (signal?.aborted) return;
    if (error) {
      setEstimators([]);
      setTotalCount(0);
      showToast(apiErrorMessage(error, 'Could not load accounts'), 'error');
      return;
    }
    setEstimators(parseEstimators(data));
    setTotalCount(estimatorsCount(data));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    loadEstimators({ q: query, page, signal: controller.signal }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [query, page, reloadKey, loadEstimators]);

  const applyCompanies = (userId, companies) => {
    if (!userId || !companies) return;
    setEstimators(prev =>
      prev.map(item =>
        item.id === userId ? { ...item, companies, raw: { ...item.raw, assigned_companies: companies } } : item
      )
    );
  };

  const handleSearchChange = event => {
    if (createOpen) return;
    setSearch(event.target.value);
  };
  const openCreate = () => setCreateOpen(true);
  const closeCreate = () => setCreateOpen(false);
  const handleCreated = () => {
    setCreateOpen(false);
    setPage(1);
    setReloadKey(key => key + 1);
  };
  const handlePageChange = (_, nextPage) => setPage(nextPage);
  const closeCompanies = () => setCompaniesFor(null);
  const handleCompaniesSaved = updated => {
    applyCompanies(companiesFor.id, companiesFromUpdate(updated));
    setCompaniesFor(null);
  };

  const handleToggleActive = async estimator => {
    if (!estimator?.id || updatingId) return;
    const nextQualifyOps = !estimator.isActive;
    setUpdatingId(estimator.id);
    try {
      const { data, error } = await updateSniperEstimator({ userId: estimator.id, isQualifyOps: nextQualifyOps });
      if (error) {
        showToast(apiErrorMessage(error, `Could not ${nextQualifyOps ? 'enable' : 'disable'} account`), 'error');
        return;
      }
      showToast(nextQualifyOps ? 'Account enabled' : 'Account disabled', 'success');
      const companies = companiesFromUpdate(data);
      setEstimators(prev =>
        prev.map(item =>
          item.id === estimator.id
            ? {
                ...item,
                isActive: nextQualifyOps,
                companies: companies || item.companies,
                raw: { ...item.raw, is_qualify_ops: nextQualifyOps, assigned_companies: companies || item.companies }
              }
            : item
        )
      );
    } catch {
      showToast(`Could not ${nextQualifyOps ? 'enable' : 'disable'} account`, 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <FormContainer>
      <WideCard className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
        <div className='flex flex-wrap items-center justify-between gap-4 px-6 py-5'>
          <h2 className='m-0 text-xl font-semibold text-gray-900'>Internal Team Access</h2>
          <div className='flex flex-wrap items-center gap-3'>
            <Input
              value={search}
              className='w-[260px]'
              name='estimator-list-search'
              autoComplete='off'
              placeholder='Search name or email'
              inputProps={{ autoComplete: 'off' }}
              onChange={handleSearchChange}
            />
            <Button color='primary' onClick={openCreate}>
              + Create account
            </Button>
          </div>
        </div>

        <EstimatorsTable
          loading={loading}
          estimators={estimators}
          updatingId={updatingId}
          page={page}
          pageCount={pageCount}
          onPageChange={handlePageChange}
          onCompanies={setCompaniesFor}
          onToggleActive={handleToggleActive}
        />
      </WideCard>

      {createOpen && <AccountModal open mode='create' onClose={closeCreate} onSaved={handleCreated} />}

      {companiesFor && (
        <AccountModal
          open
          mode='companies'
          estimator={companiesFor}
          onClose={closeCompanies}
          onSaved={handleCompaniesSaved}
        />
      )}
    </FormContainer>
  );
};

export default InternalTeamAccess;
